/**
 * MCQ Set grading for ContentPlayer (shared with MCQSetPlayer step mode).
 */

import type { ContentGrader, GradingResult, ScoringConfig } from '@ai-content-hub/content-core';
import { mcqGrader, isMcqAnswered } from '../mcq/grader';
import type { MCQAnswer } from '../mcq/schema';
import type { MCQSetData, MCQSetScoringConfig } from './schema';

export interface MCQSetItemResult {
    itemId: string;
    answer: MCQAnswer | undefined;
    isCorrect: boolean;
    score: number;
    max: number;
}

export interface MCQSetResults {
    items: MCQSetItemResult[];
    totalScore: number;
    max: number;
    percentage: number;
    passed: boolean;
}

export function validateMCQSetAnswer(
    answer: unknown,
    data: MCQSetData,
    opts?: { allowPartial?: boolean }
): boolean {
    if (!data?.items?.length) return false;
    if (!answer || typeof answer !== 'object' || Array.isArray(answer)) return false;
    const rec = answer as Record<string, unknown>;
    // Forced submit: grade what was answered (blanks score 0); nothing answered stays "not done".
    if (opts?.allowPartial) return data.items.some((item) => isMcqAnswered(rec[item.id]));
    return data.items.every((item) => isMcqAnswered(rec[item.id]));
}

export function computeMCQSetResults(
    data: MCQSetData,
    answers: Record<string, MCQAnswer | undefined>,
    scoring?: MCQSetScoringConfig & ScoringConfig,
    contentConfig?: { scoring?: MCQSetScoringConfig & ScoringConfig }
): MCQSetResults {
    const merged = { ...contentConfig?.scoring, ...scoring };
    const pointsPerQuestion = merged.pointsPerQuestion ?? merged.max ?? 1;
    const passingThreshold = merged.passingThreshold ?? merged.passingScore ?? 0.7;
    const partialCredit = merged.partialCreditEnabled ?? true;

    const itemResults: MCQSetItemResult[] = data.items.map((item) => {
        const answer = answers[item.id];
        const gradeResult = mcqGrader.grade(item.data, answer, {
            max: pointsPerQuestion,
            partialCredit,
        });
        return {
            itemId: item.id,
            answer,
            isCorrect: gradeResult.isCorrect,
            score: gradeResult.score,
            max: gradeResult.max,
        };
    });

    const totalScore = itemResults.reduce((sum, r) => sum + r.score, 0);
    const max = itemResults.reduce((sum, r) => sum + r.max, 0);
    const percentage = max > 0 ? totalScore / max : 0;
    const passed = percentage >= passingThreshold;

    return {
        items: itemResults,
        totalScore,
        max,
        percentage,
        passed,
    };
}

export function gradeMCQSetContent(
    data: MCQSetData,
    answer: unknown,
    scoring?: ScoringConfig,
    context?: { config?: { scoring?: MCQSetScoringConfig & ScoringConfig } }
): GradingResult {
    const answers =
        answer && typeof answer === 'object' && !Array.isArray(answer)
            ? (answer as Record<string, MCQAnswer | undefined>)
            : {};

    const results = computeMCQSetResults(data, answers, scoring as MCQSetScoringConfig & ScoringConfig, context?.config);

    const correctCount = results.items.filter((r) => r.isCorrect).length;

    return {
        isCorrect: results.passed,
        score: results.totalScore,
        max: results.max,
        details: {
            items: results.items,
            correctCount,
            totalItems: results.items.length,
            mcqSetResults: results,
            passed: results.passed,
            percentage: results.percentage,
        },
    };
}

export const mcqSetContentGrader: ContentGrader<MCQSetData, Record<string, MCQAnswer | undefined>> =
    {
        grade: (data, answer, scoring, context) =>
            gradeMCQSetContent(data, answer, scoring, { config: context?.config }),
        validateAnswer: (answer, data, opts) => validateMCQSetAnswer(answer, data, opts),
    };
